import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { isDarkColor } from 'common/utilities/colors/colors';
import { arrayTimes } from 'common/utilities/core/array';
import { sx } from 'core/utilities/theme';
import React from 'react';
import type { EntitySequence as Sequence, SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { SHOW_PRINT_VIEW } from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';
import type { ColumnsLetterOccurrences } from 'sequenceAnalyser/utilities/colorCalculations/colorCalculations';
import type { SelectedColorScheme } from 'sequenceAnalyser/utilities/colors';
import { groupFeaturesByName } from 'sequenceAnalyser/utilities/features/features';
import type { FeatureDefinitions, SequenceAnalyserTableView } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import {
  buildFeatureId, hasAnyFeatureDefinitionVisible, longestSequence, unAlignedResiduePosition,
} from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

import classes, { pagePrintStyles } from './PrintView.style';
import PrintViewColors from './PrintViewColors/PrintViewColors';
import PrintViewResidue from './PrintViewResidue/PrintViewResidue';
import PrintViewSummary from './PrintViewSummary/PrintViewSummary';

interface ComponentProps {
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  featureDefinitions: FeatureDefinitions;
  view: SequenceAnalyserTableView;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  colors?: SelectedColorScheme;
  expandedFeaturesForSequences: number[];
  maskingResidue: string;
}

function PrintView(props: ComponentProps) {
  const {
    sequences, alignedSequences, featureDefinitions, colors, view,
    columnsLetterOccurrences, expandedFeaturesForSequences, maskingResidue,
  } = props;

  const residuesPerChunk = 33;
  const longestSequenceLength = longestSequence(alignedSequences)?.residues.length || 0;
  const numberOfChunks = Math.ceil(longestSequenceLength / residuesPerChunk);
  const alignedSequencesChunks = arrayTimes(numberOfChunks).map((chunk, i) => {
    const newSequences = alignedSequences.map((s) => ({
      id: s.id,
      residues: s.residues.slice(i * residuesPerChunk, i * residuesPerChunk + residuesPerChunk),
    }));
    return newSequences;
  });

  const getSequence = (alignedSequence: SequenceAlign) => (
    sequences.find((s) => s.id === alignedSequence.id) as Sequence
  );

  const positionFontSize = (position: number) => {
    if (position.toString().length === 4) return '11px';
    if (position.toString().length === 5) return '9px';
    return '14px';
  };

  const hasFeatures = hasAnyFeatureDefinitionVisible(featureDefinitions);

  return (
    <>
      <GlobalStyles styles={pagePrintStyles} />

      <Box sx={classes.root} display={SHOW_PRINT_VIEW ? 'block' : 'none'} displayPrint={{ xs: 'block' }}>
        <PrintViewSummary alignment="Default" range="All" colors={colors} />

        <Box sx={classes.pagebreak} />

        {alignedSequencesChunks.map((alignedSequencesChunk, chunkPosition) => {
          const chunkFirstResiduePosition = (chunkPosition * residuesPerChunk);
          const chunkLastResiduePosition = chunkFirstResiduePosition + residuesPerChunk;

          return (
            <Table
               
              key={`alignedSequencesChunk-${chunkPosition}`}
              size="small"
              sx={classes.table}
            >
              <TableBody>
                {alignedSequencesChunk.map((alignedSequence, rowId) => {
                  const sequence = getSequence(alignedSequence);

                  const lastResiduePositionInAlignedSequenceChunk = unAlignedResiduePosition(
                    alignedSequences,
                    alignedSequence.id,
                    chunkLastResiduePosition,
                  );

                  const showExpandedFeatures = expandedFeaturesForSequences.includes(alignedSequence.id);

                  const visibleFeatures = sequence.features
                    .filter((feature) => featureDefinitions[buildFeatureId(feature)].visible);

                  const visibleFeaturesWithinChunk = visibleFeatures.filter((f) => (
                    Math.max(f.start, chunkFirstResiduePosition) <= Math.min(f.stop, chunkLastResiduePosition)
                  ));

                  const featuresGroups = groupFeaturesByName(visibleFeaturesWithinChunk);

                  const fragmentKey = `alignedSequencesChunk-${rowId}`;

                  return (
                    <React.Fragment key={fragmentKey}>
                      <TableRow sx={classes.row}>
                        <TableCell
                          size="small"
                          sx={sx(classes.cell, classes.nameCell)}
                        >
                          {getSequence(alignedSequence).name}
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={sx(classes.cell, classes.residuesCell)}
                        >
                          {alignedSequence.residues.split('').map((residue, residuePositionInChunk) => (
                            <PrintViewResidue
                               
                              key={`residue-${alignedSequence.id}-${residuePositionInChunk}`}
                              featureDefinitions={featureDefinitions}
                              view={view}
                              columnsLetterOccurrences={columnsLetterOccurrences}
                              colors={colors}
                              residue={residue}
                              chunkPosition={chunkPosition}
                              residuePositionInChunk={residuePositionInChunk}
                              rowId={rowId}
                              features={visibleFeaturesWithinChunk}
                              noFeaturesColors={showExpandedFeatures}
                              hasFeatures={hasFeatures}
                              sequenceId={alignedSequence.id}
                              residuesPerChunk={residuesPerChunk}
                              maskingResidue={maskingResidue}
                            />
                          ))}
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={sx(classes.cell, classes.positionCell)}
                          style={{ fontSize: positionFontSize(lastResiduePositionInAlignedSequenceChunk) }}
                        >
                          {lastResiduePositionInAlignedSequenceChunk}
                        </TableCell>
                      </TableRow>
                      {showExpandedFeatures && Object.values(featuresGroups).map((features) => {
                        const feature = features[0];
                        const bgColor = featureDefinitions[buildFeatureId(feature)].color;

                        return (
                          <TableRow
                             
                            key={`alignedSequencesChunk-${rowId}-feature-${buildFeatureId(feature)}`}
                            sx={classes.row}
                          >
                            <TableCell
                              size="small"
                              sx={sx(
                                classes.cell,
                                classes.nameCell,
                              )}
                              className={[
                                !isDarkColor(bgColor) ? 'lightBg' : 'darkBg',
                              ].join(' ')}
                              style={{
                                backgroundColor: `rgb(${bgColor.join(',')})`,
                              }}
                            >
                              {feature.name}
                            </TableCell>
                            <TableCell
                              size="small"
                              sx={(classes.cell, classes.residuesCell)}
                            >
                              {/* Iterate again over residues, this time only showing given features */}
                              {alignedSequence.residues.split('').map((residue, residuePositionInChunk) => (
                                <PrintViewResidue
                                   
                                  key={`residue-${alignedSequence.id}-${residuePositionInChunk}`}
                                  featureDefinitions={featureDefinitions}
                                  view={view}
                                  columnsLetterOccurrences={columnsLetterOccurrences}
                                  residue={residue}
                                  chunkPosition={chunkPosition}
                                  residuePositionInChunk={residuePositionInChunk}
                                  rowId={rowId}
                                  features={features}
                                  noFeaturesColors={false}
                                  hasFeatures={hasFeatures}
                                  sequenceId={alignedSequence.id}
                                  residuesPerChunk={residuesPerChunk}
                                  hideResidueForView
                                  maskingResidue={maskingResidue}
                                />
                              ))}
                            </TableCell>
                            <TableCell
                              size="small"
                              sx={sx(classes.cell, classes.positionCell)}
                              style={{ fontSize: positionFontSize(lastResiduePositionInAlignedSequenceChunk) }}
                            >
                              {lastResiduePositionInAlignedSequenceChunk}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          );
        })}

        {colors && (
          <PrintViewColors colors={colors} />
        )}
      </Box>
    </>
  );
}

export default PrintView;
